import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { OptOutOfCampaignRPC } from "@pulse/pulse-rpcs/lib/email-cycle/optOutOfCampaignRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useOptOutOfCampaignRPCClientImpl } from "../rpcs/RPC";
import { OptOutReason } from "@pulse/pulse-rpcs/lib/email-cycle/optOutReason";

export enum OptOutReasonEnum {
  NotRightPerson = "NOT_RIGHT_PERSON",
  NotInterested = "NOT_INTERESTED",
  TooFrequent = "TOO_FREQUENT",
  EmailsNotCorrectlyRendered = "EMAILS_NOT_CORRECTLY_RENDERED",
  PrivacyConcerns = "PRIVACY_CONCERNS",
  Other = "OTHER",
}

const getInitialState = (): SnapshotIn<typeof OptOutOfCampaignStore> => ({
  reason: new OptOutReason.NotInterested(),
  loadingOptOutOfCampaign: false,
  optOutOfCampaignRPCError: null,
});

export const OptOutOfCampaignStore = types
  .model("OptOutOfCampaignStore", {
    reason: types.frozen<OptOutReason.OptOutReason>(),
    loadingOptOutOfCampaign: types.boolean,
    optOutOfCampaignRPCError: types.maybeNull(
      types.enumeration(
        "OptOutOfCampaignRPCError",
        Object.values(OptOutOfCampaignRPC.RPCError),
      ),
    ),
  })
  .views((store) => ({
    get description(): string | null {
      return store.reason instanceof OptOutReason.Other
        ? store.reason.description
        : null;
    },
    get reasonEnum(): OptOutReasonEnum {
      if (store.reason instanceof OptOutReason.EmailsNotCorrectlyRendered) {
        return OptOutReasonEnum.EmailsNotCorrectlyRendered;
      } else if (store.reason instanceof OptOutReason.NotInterested) {
        return OptOutReasonEnum.NotInterested;
      } else if (store.reason instanceof OptOutReason.NotRightPerson) {
        return OptOutReasonEnum.NotRightPerson;
      } else if (store.reason instanceof OptOutReason.Other) {
        return OptOutReasonEnum.Other;
      } else if (store.reason instanceof OptOutReason.PrivacyConcerns) {
        return OptOutReasonEnum.PrivacyConcerns;
      } else if (store.reason instanceof OptOutReason.TooFrequent) {
        return OptOutReasonEnum.TooFrequent;
      } else {
        return store.reason satisfies never;
      }
    },
  }))
  .actions((store) => ({
    setDescription: (description: string): void => {
      store.reason = new OptOutReason.Other(
        description.trim() ? description : null,
      );
    },
    setReason: (reason: string): void => {
      switch (reason) {
        case OptOutReasonEnum.EmailsNotCorrectlyRendered:
          store.reason = new OptOutReason.EmailsNotCorrectlyRendered();
          break;
        case OptOutReasonEnum.NotInterested:
          store.reason = new OptOutReason.NotInterested();
          break;
        case OptOutReasonEnum.NotRightPerson:
          store.reason = new OptOutReason.NotRightPerson();
          break;
        case OptOutReasonEnum.Other:
          store.reason = new OptOutReason.Other();
          break;
        case OptOutReasonEnum.PrivacyConcerns:
          store.reason = new OptOutReason.PrivacyConcerns();
          break;
        case OptOutReasonEnum.TooFrequent:
          store.reason = new OptOutReason.TooFrequent();
          break;
        default:
          throw new Error(`Invalid reason value: ${reason}`);
      }
    },
    getOptOutOfCampaign: flow(function* (emailId: string) {
      try {
        store.loadingOptOutOfCampaign = true;
        store.optOutOfCampaignRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new OptOutOfCampaignRPC.Request(
          new LeoUUID(emailId),
          store.reason,
        );

        const result: LeoRPCResult<
          OptOutOfCampaignRPC.Response,
          OptOutOfCampaignRPC.Errors.Errors
        > = yield useOptOutOfCampaignRPCClientImpl(apiClient).execute(request);

        if ("error" in result) {
          switch (result.error.code) {
            case OptOutOfCampaignRPC.RPCError.InvalidEmailId:
              store.optOutOfCampaignRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingOptOutOfCampaign = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createOptOutOfCampaignStore = (): Instance<
  typeof OptOutOfCampaignStore
> => OptOutOfCampaignStore.create(getInitialState());
